import { addDays, format, startOfDay } from 'date-fns';
import type { CalendarAbsence } from '@/hooks/useBookings';

function datePart(iso: string): string {
  return iso.slice(0, 10);
}

/**
 * Whole-day absence bounds are stored as a date-only string or UTC midnight
 * (`YYYY-MM-DDT00:00:00…`). Any other time of day is a partial-day absence.
 */
function isUtcMidnightBound(iso: string): boolean {
  const time = iso.slice(11, 19);
  if (time.length === 0) return true;
  return time === '00:00:00';
}

function absenceCoversDateFully(absence: CalendarAbsence, date: string): boolean {
  if (datePart(absence.startDate) > date) return false;
  if (absence.endDate !== null && datePart(absence.endDate) < date) return false;
  if (datePart(absence.startDate) === date && !isUtcMidnightBound(absence.startDate)) {
    return false;
  }
  if (
    absence.endDate !== null
    && datePart(absence.endDate) === date
    && !isUtcMidnightBound(absence.endDate)
  ) {
    return false;
  }
  return true;
}

function visibleDates(
  start: Date,
  end: Date,
  isOpenDay?: (date: Date) => boolean,
): string[] {
  const dates: string[] = [];
  for (let day = startOfDay(start); day < end; day = addDays(day, 1)) {
    if (isOpenDay !== undefined && !isOpenDay(day)) continue;
    dates.push(format(day, 'yyyy-MM-dd'));
  }
  return dates;
}

/**
 * Mechanic ids whose absences cover every visible open day in `[start, end)`.
 *
 * Day view is one working day; week view is every day the calendar window
 * already includes. Closed days (typically the weekend) are skipped when
 * `isOpenDay` is provided so a Mon–Fri holiday still hides the mechanic
 * (#2910). If every visible day is closed, nobody is hidden. Without
 * `isOpenDay` every calendar day in the window must be covered (#2902).
 * A partial-day absence on any counted day keeps the mechanic visible.
 */
export function mechanicIdsAbsentForRange({
  start,
  end,
  absences,
  isOpenDay,
}: {
  start: Date;
  end: Date;
  absences: readonly CalendarAbsence[];
  isOpenDay?: (date: Date) => boolean;
}): string[] {
  const dates = visibleDates(start, end, isOpenDay);
  if (dates.length === 0) return [];

  const byMechanic = new Map<string, CalendarAbsence[]>();
  for (const absence of absences) {
    const list = byMechanic.get(absence.employeeId);
    if (list) {
      list.push(absence);
    } else {
      byMechanic.set(absence.employeeId, [absence]);
    }
  }

  const ids: string[] = [];
  for (const [mechanicId, mechanicAbsences] of byMechanic) {
    const fullyAbsent = dates.every((date) =>
      mechanicAbsences.some((absence) => absenceCoversDateFully(absence, date)),
    );
    if (fullyAbsent) ids.push(mechanicId);
  }
  return ids;
}
